import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { api } from "@/lib/api";
import { optimizeImage } from "@/lib/img";
import { ArrowUpRight, ArrowLeft, Linkedin, Facebook, Twitter, Mail, MessageCircle, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { openVipModal } from "@/lib/vipModal";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data);
        // load related (same category, exclude self)
        try {
          const { data: list } = await api.get("/blog", {
            params: { category: data.category, limit: 6 },
          });
          setRelated(list.filter((p) => p.slug !== slug).slice(0, 3));
        } catch {
          /* noop */
        }
        // SEO meta
        document.title = `${data.title} — LocalizeHER`;
        if (data.meta_description) {
          let tag = document.querySelector('meta[name="description"]');
          if (!tag) {
            tag = document.createElement("meta");
            tag.setAttribute("name", "description");
            document.head.appendChild(tag);
          }
          tag.setAttribute("content", data.meta_description);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
  }, [slug]);

  const onShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: post?.title, text: post?.excerpt, url });
        return;
      } catch {
        /* user cancelled */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" data-testid="blog-post-loading">
        <MarketingHeader />
        <div
          className="pt-40 text-center overline"
          style={{ background: "var(--lh-espresso-noir)", color: "rgba(234,216,199,0.6)", minHeight: "60vh" }}
        >
          Loading…
        </div>
        <MarketingFooter />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen" data-testid="blog-post-notfound">
        <MarketingHeader />
        <div
          className="pt-44 pb-32 text-center"
          style={{ background: "var(--lh-espresso-noir)", color: "var(--lh-warm-ivory)" }}
        >
          <div className="overline mb-4" style={{ color: "var(--lh-peach)" }}>
            404
          </div>
          <h1 className="font-display-bold text-5xl tracking-tighter">
            We can't find that piece.
          </h1>
          <Link to="/blog" className="mt-8 inline-flex items-center gap-2 btn-mauve">
            <ArrowLeft size={14} /> Back to the journal
          </Link>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  const shareUrl = encodeURIComponent(window.location.href);
  const shareText = encodeURIComponent(post.title);

  return (
    <div className="min-h-screen" data-testid="blog-post-page">
      <MarketingHeader />

      {/* Hero */}
      <section
        className="pt-32 lg:pt-40 pb-12 lg:pb-16 section-light"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 overline mb-8"
            style={{ color: "var(--lh-cognac)" }}
          >
            <ArrowLeft size={12} /> The Journal
          </Link>
          <div className="overline mb-5" style={{ color: "var(--lh-cognac)" }}>
            {post.category} · {post.read_time} min read
          </div>
          <h1
            className="font-display-bold text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter"
            data-testid="blog-post-title"
          >
            {post.title}
          </h1>
          <p
            className="mt-7 text-lg lg:text-2xl font-medium leading-snug"
            style={{ color: "var(--lh-espresso-noir)" }}
          >
            {post.excerpt}
          </p>
          <div
            className="mt-8 flex items-center gap-4 text-xs uppercase tracking-wider"
            style={{ color: "var(--lh-greige)" }}
          >
            <span>{post.author}</span>
            <span>·</span>
            <span>{new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          </div>
        </div>
      </section>

      {/* Cover */}
      {post.cover_image && (
        <div className="section-light">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 -mt-2">
            <img
              src={optimizeImage(post.cover_image, { w: 1400, name: `localizeher-blog-${post.slug || "post"}` })}
              alt={post.title}
              className="w-full h-[280px] md:h-[440px] lg:h-[560px] object-cover rounded-2xl"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              data-testid="blog-post-cover"
            />
          </div>
        </div>
      )}

      {/* Body */}
      <section className="py-12 lg:py-16 section-light" data-testid="blog-post-body-section">
        <div className="max-w-[760px] mx-auto px-6 md:px-10 lg:px-14">
          <article className="prose-lh" data-testid="blog-post-body">
            <ReactMarkdown>{post.body}</ReactMarkdown>
          </article>

          {/* Tags + share */}
          <div
            className="mt-14 pt-10 flex flex-wrap items-center justify-between gap-6"
            style={{ borderTop: "1px solid rgba(234,216,199,0.18)" }}
          >
            <div className="flex flex-wrap gap-2">
              {(post.tags || []).map((t) => (
                <span
                  key={t}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider"
                  style={{
                    background: "rgba(255,168,115,0.18)",
                    color: "var(--lh-peach)",
                    border: "1px solid var(--lh-peach)",
                  }}
                  data-testid={`blog-tag-${t.replace(/\s+/g, "-")}`}
                >
                  {t}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="blog-post-share-bar">
              <span className="overline text-[var(--lh-muted)] mr-2 hidden sm:inline">Share</span>
              <button
                onClick={onShare}
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-[var(--lh-espresso-noir)] hover:text-[var(--lh-espresso-noir)] hover:bg-[var(--lh-peach)] transition-colors sm:hidden"
                style={{ border: "1px solid var(--lh-border)" }}
                aria-label="Share this post"
                data-testid="blog-post-share"
                title="Share"
              >
                <Share2 size={15} />
              </button>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-[var(--lh-espresso-noir)] hover:text-[var(--lh-espresso-noir)] hover:bg-[var(--lh-peach)] transition-colors"
                style={{ border: "1px solid var(--lh-border)" }}
                aria-label="Share on Facebook"
                data-testid="blog-share-facebook"
                title="Share on Facebook"
              >
                <Facebook size={15} />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-[var(--lh-espresso-noir)] hover:text-[var(--lh-espresso-noir)] hover:bg-[var(--lh-peach)] transition-colors"
                style={{ border: "1px solid var(--lh-border)" }}
                aria-label="Share on X"
                data-testid="blog-share-twitter"
                title="Share on X"
              >
                <Twitter size={15} />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-[var(--lh-espresso-noir)] hover:text-[var(--lh-espresso-noir)] hover:bg-[var(--lh-peach)] transition-colors"
                style={{ border: "1px solid var(--lh-border)" }}
                aria-label="Share on LinkedIn"
                data-testid="blog-share-linkedin"
                title="Share on LinkedIn"
              >
                <Linkedin size={15} />
              </a>
              <a
                href={`https://api.whatsapp.com/send?text=${shareText}%20${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-[var(--lh-espresso-noir)] hover:text-[var(--lh-espresso-noir)] hover:bg-[var(--lh-peach)] transition-colors"
                style={{ border: "1px solid var(--lh-border)" }}
                aria-label="Share on WhatsApp"
                data-testid="blog-share-whatsapp"
                title="Share on WhatsApp"
              >
                <MessageCircle size={15} />
              </a>
              <a
                href={`mailto:?subject=${shareText}&body=${shareText}%20-%20${shareUrl}`}
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-[var(--lh-espresso-noir)] hover:text-[var(--lh-espresso-noir)] hover:bg-[var(--lh-peach)] transition-colors"
                style={{ border: "1px solid var(--lh-border)" }}
                aria-label="Share via email"
                data-testid="blog-share-email"
                title="Share via email"
              >
                <Mail size={15} />
              </a>
              <button
                onClick={copyLink}
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-[var(--lh-espresso-noir)] hover:text-[var(--lh-espresso-noir)] hover:bg-[var(--lh-peach)] transition-colors"
                style={{ border: "1px solid var(--lh-border)" }}
                aria-label="Copy link"
                data-testid="blog-share-copy"
                title="Copy link"
              >
                <Link2 size={15} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-14 lg:py-18 text-center section-light" data-testid="blog-cta-strip">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-14">
          <h2 className="font-display-bold text-4xl md:text-5xl tracking-tighter leading-[1.0]">
            Build with women <em>who do.</em>
          </h2>
          <p className="mt-5 text-base lg:text-lg font-medium">
            Membership unlocks the full marketing library, the community, and the directory placement.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => openVipModal("interested", "blog-post")}
              className="btn-mauve"
              data-testid="blog-cta-join"
            >
              Reserve Your VIP Spot <ArrowUpRight size={14} />
            </button>
            <Link to="/her-guide" className="btn-ghost-light">
              Explore The HER Guide
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section
          className="py-20 lg:py-24 section-light"
          style={{ borderTop: "1px solid var(--lh-border)" }}
          data-testid="blog-related"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
            <div className="overline mb-4" style={{ color: "var(--lh-cognac)" }}>
              Keep reading
            </div>
            <h3 className="font-display-bold text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-12">
              More from the journal.
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group block"
                  data-testid={`blog-related-${p.slug}`}
                >
                  <div className="overflow-hidden rounded-xl mb-5 aspect-[4/3]">
                    {p.cover_image && (
                      <img
                        src={optimizeImage(p.cover_image, { w: 500, name: `localizeher-blog-${p.slug || "post"}` })}
                        alt={p.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                      />
                    )}
                  </div>
                  <div className="overline mb-3" style={{ color: "var(--lh-peach)" }}>
                    {p.category} · {p.read_time} min
                  </div>
                  <h4 className="font-display-bold text-xl md:text-2xl tracking-tighter leading-[1.05] text-[var(--lh-warm-ivory)] group-hover:text-[var(--lh-peach)] transition-colors">
                    {p.title}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <MarketingFooter />
    </div>
  );
}
